import * as React from 'react'
import type { Placement } from '@floating-ui/react'
import classNames from 'classnames'
import { Select } from 'radix-ui'
import { ChevronLeft } from 'components/icons/ChevronLeft'
import type { FormFieldDefaults } from './types'
import { FormLabel } from './form/FormLabel'
import { FormError } from './form/FormError'
import { fixedForwardRef } from 'util/refs'
import { useTranslationTyped } from 'i18n/i18n'

export type SelectOption<T> = {
  label: string | React.ReactNode
  value: T
  selectedLabel?: string | React.ReactNode
  metadata?: Record<string, string | number | boolean>
  key?: string | number
  isUnchoosable?: boolean // Useful if the currently selected option is a legacy option
  disabled?: boolean
}

type SelectOptionGroupLabel = {
  groupLabel: string | React.ReactNode
}

export interface BreakoutSelectProps<T> extends FormFieldDefaults<'select'> {
  options:
    | SelectOption<T>[]
    | Array<{
        groupLabel: string | React.ReactNode
        options: SelectOption<T>[]
      }>
  loading?: boolean
  // kind secondary is the default and represents the "primary" style in figma
  // tertiary is the secondary style in figma
  kind?: 'secondary' | 'tertiary' | 'accent'
  variant?: 'default' | 'small'
  onChange?: (value: T) => void
  fullWidth?: boolean
  inputClassName?: string
  labelComponent?: React.ReactNode
  icon?: React.ReactNode
  showChevron?: boolean
  allowClear?: boolean
  placeholder?: string
  emptyMessage?: string | React.ReactNode
  maxHeight?: number
  placement?: Placement
  onOuterKeyDown?: React.KeyboardEventHandler<HTMLDivElement>
  noTruncateSelectedLabel?: boolean
  borderBetweenOptions?: boolean
}

const DEFAULT_SELECT_MAX_HEIGHT = 500

function placementToRadix(placement: Placement): {
  side: 'top' | 'right' | 'bottom' | 'left'
  align: 'start' | 'center' | 'end'
} {
  const [side, align] = placement.split('-')
  let resolvedSide: 'top' | 'right' | 'bottom' | 'left' = 'right'
  if (side === 'top') resolvedSide = 'top'
  else if (side === 'bottom') resolvedSide = 'bottom'
  else if (side === 'left') resolvedSide = 'left'
  else if (side === 'right') resolvedSide = 'right'

  let resolvedAlign: 'start' | 'center' | 'end' = 'center'
  if (align === 'start') resolvedAlign = 'start'
  else if (align === 'end') resolvedAlign = 'end'
  else if (align === 'center') resolvedAlign = 'center'

  return { side: resolvedSide, align: resolvedAlign }
}

export const BreakoutSelect = fixedForwardRef(function BreakoutSelect<
  T = string,
>(
  {
    disabled,
    loading = false,
    kind = 'tertiary',
    showChevron = true,
    name,
    label,
    labelComponent,
    labelClass,
    testId,
    options: _options,
    icon,
    error,
    errorClass,
    required,
    value,
    inputClassName,
    placeholder,
    allowClear,
    onChange,
    maxHeight,
    fullWidth,
    className,
    'aria-label': ariaLabel,
    emptyMessage,
    placement = 'bottom-start',
    onOuterKeyDown,
    variant = 'default',
    noTruncateSelectedLabel = false,
    borderBetweenOptions = false,
    id,
    ...rest
  }: BreakoutSelectProps<T>,
  fwdRef: React.ForwardedRef<HTMLSelectElement>
) {
  const optionsWithGroupLabels = React.useMemo(() => {
    if (!_options.length || !('groupLabel' in _options[0])) return _options
    const groupedOptions = _options as Array<{
      groupLabel: string | React.ReactNode
      options: SelectOption<T>[]
    }>

    const optionsWithLabels: Array<SelectOption<T> | SelectOptionGroupLabel> =
      []

    groupedOptions.forEach((group) => {
      // don't render group options if no label
      if (!group.options.length) return

      optionsWithLabels.push({
        groupLabel: group.groupLabel,
      })

      group.options.forEach((option) => {
        optionsWithLabels.push(option)
      })
    })

    return optionsWithLabels
  }, [_options])

  const { selectableOptions, valueToOptionMap, selectedRadixValue } =
    React.useMemo(() => {
      const selectableOptions: SelectOption<T>[] = []
      const valueToOptionMap = new Map<string, T>()

      optionsWithGroupLabels.forEach((option) => {
        if ('groupLabel' in option || option.isUnchoosable) return

        const radixValue = `opt-${selectableOptions.length}`
        selectableOptions.push(option)
        valueToOptionMap.set(radixValue, option.value)
      })

      const selectedValue =
        value !== undefined && value !== null && value !== ''
          ? selectableOptions.find((opt) => opt.value === value)
          : undefined
      const selectedRadixValue = selectedValue
        ? `opt-${selectableOptions.indexOf(selectedValue)}`
        : ''

      return {
        selectableOptions,
        valueToOptionMap,
        selectedRadixValue,
      }
    }, [optionsWithGroupLabels, value])

  const selectedOption = React.useMemo(() => {
    return selectableOptions.find((opt) => opt.value === value)
  }, [selectableOptions, value])

  const selectedLabel = selectedOption?.selectedLabel ?? selectedOption?.label

  const { side, align } = placementToRadix(placement)

  const dataTestId = testId || (name ? `${name}-select` : undefined)
  const generatedId = React.useId()
  const triggerId = id || generatedId

  const hasGroups = React.useMemo(
    () =>
      optionsWithGroupLabels.length > 0 &&
      'groupLabel' in optionsWithGroupLabels[0],
    [optionsWithGroupLabels]
  )

  const groupsForRender = React.useMemo((): Array<{
    label: React.ReactNode
    options: SelectOption<T>[]
  }> => {
    if (!hasGroups) {
      const opts = optionsWithGroupLabels.filter(
        (o): o is SelectOption<T> => 'value' in o && !o.isUnchoosable
      )
      return [{ label: null, options: opts }]
    }
    const result: Array<{
      label: React.ReactNode
      options: SelectOption<T>[]
    }> = []
    let current: { label: React.ReactNode; options: SelectOption<T>[] } | null =
      null

    for (const item of optionsWithGroupLabels) {
      if ('groupLabel' in item) {
        if (current !== null && current.options.length > 0) {
          result.push(current)
        }
        current = { label: item.groupLabel, options: [] }
      } else if ('value' in item && !item.isUnchoosable) {
        const option = item as SelectOption<T>
        if (current !== null) {
          current.options.push(option)
        } else {
          current = { label: null, options: [option] }
        }
      }
    }
    if (current !== null && current.options.length > 0) {
      result.push(current)
    }
    return result
  }, [optionsWithGroupLabels, hasGroups])

  const { t, tt } = useTranslationTyped()

  const handleValueChange = React.useCallback(
    (radixValue: string) => {
      if (radixValue === undefined) {
        onChange?.('' as T)
        return
      }
      const optionValue = valueToOptionMap.get(radixValue)
      if (optionValue !== undefined) {
        onChange?.(optionValue)
      }
    },
    [onChange, valueToOptionMap]
  )

  const finalAriaLabel =
    labelComponent === undefined && !!label ? undefined : ariaLabel || label

  const contentStyle = React.useMemo(() => {
    return {
      maxHeight: `${maxHeight || DEFAULT_SELECT_MAX_HEIGHT}px`,
      minWidth: 'var(--radix-select-trigger-width)',
    }
  }, [maxHeight])

  const isDisabled = disabled || loading

  const showClearButton = allowClear && value && !isDisabled

  return (
    <div
      className={classNames('flex flex-col items-start gap-1', className, {
        'flex-1': fullWidth,
      })}
      onKeyDown={onOuterKeyDown}
    >
      {labelComponent === undefined && (
        <FormLabel
          label={label}
          labelClass={labelClass}
          required={required}
          for={triggerId}
          variant={variant === 'small' ? 'compact' : variant}
        />
      )}
      {labelComponent}
      {name && (
        <select
          className="hidden"
          {...rest}
          ref={fwdRef}
          name={name}
          value={value}
          onChange={() => {}}
        />
      )}
      <Select.Root
        value={selectedRadixValue}
        onValueChange={handleValueChange}
        disabled={isDisabled}
        required={required}
        name={name ? undefined : undefined}
      >
        <div
          className={classNames(
            'flex w-full select-none items-center text-label-medium',
            {
              'rounded-lg': variant === 'small',
              'rounded-2xl': variant === 'default',
              'h-[3.25rem]': variant === 'default',
              'bg-core-secondary': kind === 'secondary' && !isDisabled,
              'bg-core-tertiary': kind === 'tertiary' && !isDisabled,
              'bg-fixed-accent-color text-core-on-secondary':
                kind === 'accent' && !isDisabled,
              'bg-surface-dim': isDisabled,
              'text-on-surface-var': isDisabled,
              'cursor-not-allowed': isDisabled,
              'cursor-pointer': !isDisabled,
              'is-disabled': isDisabled,
            }
          )}
        >
          <Select.Trigger
            data-testid={dataTestId}
            id={triggerId}
            aria-label={finalAriaLabel}
            aria-invalid={!!error}
            aria-busy={loading}
            className={classNames(
              inputClassName,
              // min-w-0 is needed to prevent the select from overflowing the
              // container when the content is too long. In conjunction with
              // overflow-hidden, it will cause the content to be truncated.
              'h-full min-w-0 flex-1 border border-outline-light',
              {
                'rounded-lg px-3 py-2': variant === 'small',
                'rounded-2xl px-4 py-1': variant === 'default',
                'rounded-br-none rounded-tr-none border-r-0': showClearButton,
                'border-core-error': !!error,
              }
            )}
          >
            <div className="group flex min-w-0 flex-1 items-center justify-between gap-2 rounded-none border-0 bg-transparent p-0 text-left outline-none [&[data-state=open]]:ring-0">
              {icon !== undefined && (
                <div className="mr-2 shrink-0">{icon}</div>
              )}
              <div
                className={classNames('min-w-0 flex-1 overflow-hidden', {
                  truncate: !noTruncateSelectedLabel,
                })}
              >
                {loading ? (
                  <div className="flex items-center">
                    <div className="h-4 w-4 animate-spin rounded-full border-2 border-core-primary border-t-transparent" />
                  </div>
                ) : (
                  <Select.Value
                    placeholder={
                      <span className="text-on-surface-var">
                        {placeholder ||
                          t('components.default_select_placeholder')}
                      </span>
                    }
                  >
                    {selectedLabel}
                  </Select.Value>
                )}
              </div>
              {showChevron && (!value || !allowClear) && !loading && (
                <Select.Icon asChild>
                  <span
                    className="rotate-270 ml-1 shrink-0 transition-transform group-data-[state=open]:rotate-90"
                    aria-hidden="true"
                  >
                    <ChevronLeft size={14} />
                  </span>
                </Select.Icon>
              )}
            </div>
          </Select.Trigger>
          {showClearButton && (
            <button
              type="button"
              className={classNames(
                'h-full border border-l-0 border-outline-light text-on-surface-var hover:text-core-error',
                {
                  'rounded-br-lg rounded-tr-lg': variant === 'small',
                  'rounded-br-2xl rounded-tr-2xl px-4 py-1':
                    variant === 'default',
                  'border-core-error': !!error,
                }
              )}
              onClick={() => {
                onChange?.('' as T)

                // After clearing the value, focus on the trigger. Otherwise,
                // after the clear button is removed, nothing will have focus.
                const trigger = document.getElementById(triggerId)
                if (trigger) {
                  trigger.focus()
                }
              }}
            >
              <strong className="clear-date text-body-medium lowercase">
                {tt.inputs.clear()}
              </strong>
            </button>
          )}
        </div>
        <Select.Portal>
          <Select.Content
            position="popper"
            side={side}
            align={align}
            sideOffset={10}
            collisionPadding={10}
            className={classNames(
              'z-[9999] min-w-24 overflow-hidden text-label-medium shadow-xl-floating',
              {
                'bg-core-secondary': kind === 'secondary' || kind === 'accent',
                'bg-core-tertiary': kind === 'tertiary',
                'rounded-lg px-2 py-1': variant === 'small',
                'rounded-2xl p-4': variant === 'default',
              }
            )}
            style={contentStyle}
            data-testid={dataTestId ? `${dataTestId}-listbox` : undefined}
          >
            <Select.Viewport className="max-h-[var(--radix-select-content-available-height)] overflow-y-auto overscroll-contain">
              {selectableOptions.length > 0 ? (
                <>
                  {groupsForRender.map((group, groupIdx) => (
                    <Select.Group key={`group-${groupIdx}`}>
                      {group.label !== null && group.label !== '' && (
                        <Select.Label
                          className="py-1 text-body-medium text-on-surface-var"
                          {...(typeof group.label === 'string'
                            ? { 'aria-label': group.label }
                            : {})}
                        >
                          {group.label}
                        </Select.Label>
                      )}
                      {group.options.map((option, optIdx) => {
                        const optIndex = selectableOptions.indexOf(option)
                        const radixValue = `opt-${optIndex}`

                        return (
                          <Select.Item
                            key={
                              option.key !== undefined
                                ? option.key
                                : ((option.value as string | number) ??
                                  `item-${groupIdx}-${optIdx}`)
                            }
                            value={radixValue}
                            disabled={option.disabled}
                            textValue={
                              typeof option.label === 'string'
                                ? option.label
                                : undefined
                            }
                            data-testid={
                              dataTestId
                                ? `${dataTestId}-option-${option.value}`
                                : undefined
                            }
                            className={classNames(
                              'cursor-pointer rounded-lg border-transparent p-4 py-2 outline-none data-[state=checked]:bg-surface-dim',
                              {
                                'cursor-not-allowed': option.disabled,
                                'data-[highlighted]:bg-core-secondary':
                                  (!kind || kind === 'tertiary') &&
                                  !option.disabled,
                                'data-[highlighted]:bg-core-tertiary':
                                  kind === 'secondary' && !option.disabled,
                                '!cursor-not-allowed text-on-surface-var':
                                  option.disabled,
                                '!rounded-t-none border-t !border-outline-light':
                                  borderBetweenOptions,
                              }
                            )}
                          >
                            <Select.ItemText>{option.label}</Select.ItemText>
                            <Select.ItemIndicator />
                          </Select.Item>
                        )
                      })}
                    </Select.Group>
                  ))}
                </>
              ) : (
                <div className="p-4 text-on-surface-disabled">
                  {emptyMessage}
                </div>
              )}
            </Select.Viewport>
          </Select.Content>
        </Select.Portal>
      </Select.Root>
      <FormError error={error} errorClass={errorClass} />
    </div>
  )
})
